import { Fragment, useEffect, useRef, useState } from 'react'
import { BellOff, ChevronDown } from 'lucide-react'
import type { ChatMessage } from '@shared/types'
import { useStore } from '../../store'
import { Message } from './Message'
import { EmptyState } from './EmptyState'

export function MessageList() {
  const messages = useStore((s) => s.messages)
  const streamingMsg = useStore((s) => s.streamingMsg)
  const activeChatId = useStore((s) => s.activeChatId)
  const scrollTarget = useStore((s) => s.scrollTarget)
  const compactedAt = useStore((s) => s.chats.find((c) => c.id === s.activeChatId)?.compactedAt ?? null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const handledNonce = useRef(-1) // last scrollTarget.n we acted on → ignore unrelated re-renders
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, streamingMsg?.content, streamingMsg?.toolEvents.length])

  // Scroll to + briefly flash a specific message when search (in-chat or global) asks for it.
  // Keyed by scrollTarget.n: each jump is handled exactly ONCE (so a later message render won't
  // re-scroll to a stale target), and depends on messages.length so a jump fired before a
  // freshly-opened chat's messages render still resolves once they arrive. The flash-removal
  // timer lives in a ref (not the effect cleanup) so a re-render mid-flash can't cancel it.
  useEffect(() => {
    if (!scrollTarget || scrollTarget.n === handledNonce.current) return
    let tries = 0
    let retryTimer: ReturnType<typeof setTimeout>
    const go = () => {
      const el = document.getElementById(`msg-${scrollTarget.id}`)
      if (el) {
        handledNonce.current = scrollTarget.n
        el.scrollIntoView({ block: 'start', behavior: 'smooth' }) // scroll-mt-24 keeps it below the header
        clearTimeout(flashTimer.current)
        document.querySelectorAll('.lb-msg-flash').forEach((n) => n.classList.remove('lb-msg-flash'))
        el.classList.add('lb-msg-flash')
        flashTimer.current = setTimeout(() => el.classList.remove('lb-msg-flash'), 1700)
      } else if (tries++ < 12) {
        retryTimer = setTimeout(go, 90)
      } else {
        handledNonce.current = scrollTarget.n // message not found (deleted?) → stop retrying
      }
    }
    go()
    return () => clearTimeout(retryTimer)
  }, [scrollTarget, messages.length])

  useEffect(() => () => clearTimeout(flashTimer.current), []) // clear a pending flash on unmount

  if (!messages.length && !streamingMsg) return <EmptyState />

  const streamMessage: ChatMessage | null = streamingMsg
    ? {
        id: streamingMsg.id,
        chatId: activeChatId ?? '',
        role: 'assistant',
        content: streamingMsg.content,
        citations: streamingMsg.citations,
        toolEvents: streamingMsg.toolEvents,
        segments: streamingMsg.segments,
        meta: {
          reasoning: streamingMsg.reasoning || undefined,
          thinkingMs: streamingMsg.thinkingMs,
          // transient — drives the live ticker; never persisted
          startedAt: streamingMsg.startedAt,
          revising: streamingMsg.revising,
          notice: streamingMsg.notice,
        } as ChatMessage['meta'] & { startedAt?: number; revising?: boolean; notice?: string },
        createdAt: Date.now(),
      }
    : null

  // The first message that's still sent verbatim to the AI (everything before it is summarized).
  const dividerBeforeId =
    compactedAt != null && messages.some((m) => m.createdAt <= compactedAt)
      ? messages.find((m) => m.createdAt > compactedAt)?.id ?? null
      : null

  // Quiet automated checks (nothing worth attention) fold into one line per run of them.
  const blocks: ({ kind: 'msg'; m: ChatMessage } | { kind: 'quiet'; items: ChatMessage[] })[] = []
  for (const m of messages) {
    if (m.meta?.quiet) {
      const last = blocks.at(-1)
      if (last?.kind === 'quiet') last.items.push(m)
      else blocks.push({ kind: 'quiet', items: [m] })
    } else blocks.push({ kind: 'msg', m })
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-6">
        {blocks.map((b) =>
          b.kind === 'quiet' ? (
            <QuietChecks key={b.items[0].id} items={b.items} />
          ) : (
            <Fragment key={b.m.id}>
              {b.m.id === dividerBeforeId && <CompactDivider />}
              <div id={`msg-${b.m.id}`} className="-mx-3 scroll-mt-24 rounded-xl px-3">
                <Message message={b.m} />
              </div>
            </Fragment>
          ),
        )}
        {streamMessage && <Message message={streamMessage} streaming />}
      </div>
    </div>
  )
}

/** A run of quiet checks: one line (how many, the latest note), open for each note. */
function QuietChecks({ items }: { items: ChatMessage[] }) {
  const [open, setOpen] = useState(false)
  const notes = items.filter((m) => m.role === 'assistant')
  const last = notes.at(-1) ?? items.at(-1)!
  const time = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  const text = (m: ChatMessage) => m.content.replace(/^🔕\s*/, '')
  return (
    <div className="text-[11.5px] text-fg-faint">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left hover:text-fg-subtle">
        <BellOff size={12} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          {notes.length > 1 ? `${notes.length} quiet checks` : 'Quiet check'} · {time(last.createdAt)} — {text(last)}
        </span>
        {notes.length > 1 && <ChevronDown size={12} className={open ? 'rotate-180' : ''} />}
      </button>
      {open && notes.length > 1 && (
        <div className="mt-1 space-y-0.5 border-l border-[var(--line-soft)] pl-3">
          {notes.map((m) => (
            <div key={m.id} id={`msg-${m.id}`} className="num truncate">
              {time(m.createdAt)} · {m.meta?.trigger?.title ? `${m.meta.trigger.title} — ` : ''}
              {text(m)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CompactDivider() {
  return (
    <div className="flex items-center gap-2 py-1 text-[11px] text-fg-faint" title="Older messages were summarized for the AI to save context. You still see them all here.">
      <span className="h-px flex-1 bg-[var(--line-soft)]" />
      <span className="shrink-0">✦ context compacted — older messages summarized</span>
      <span className="h-px flex-1 bg-[var(--line-soft)]" />
    </div>
  )
}
